export type InvitationRouteContext = {
  code: string;
  email?: string;
  idP?: string;
  // The pre-auth choice the user made on the landing page ("create" = "Create an account",
  // carried across the auth round-trip via the URL). When "create" and the post-auth
  // ActivationOptions reveals an existing profile (skip-form path), we surface the
  // "An existing profile was found" dialog instead of silently auto-activating.
  intent?: string;
};

export const getInvitationRouteContext = (search = window.location.search): InvitationRouteContext => {
  const params = new URLSearchParams(search);

  return {
    code: params.get('code') || params.get('Code') || '',
    email: params.get('email') || params.get('Email') || undefined,
    idP: params.get('idP') || params.get('idp') || undefined,
    intent: params.get('intent') || params.get('Intent') || undefined,
  };
};

// UI debug hook. Adding `?acceptinvitationdebug=<scenario>` to the post-auth page forces a specific
// error page to render so designers/devs can preview it without reproducing the underlying API
// failure. Post-auth (ActivationOptions) scenarios:
//   - googlerequired / aadrequired / msarequired / facebookrequired → the matching "sign in with …" page
//   - denied                                     → the <ActivationDeniedDialog>
//   - alreadyhasaccess                           → the <ActivationAlreadyHasAccess> page
//   - alternatemerge                             → same-provider ActivateWithAlternate, ownership proven
//                                                  (<ActivationAlternateAccountRequired variant="canMerge">)
//   - alternateblocked                           → same-provider ActivateWithAlternate, ownership not proven
//                                                  (<ActivationAlternateAccountRequired variant="blocked">)
//   - existingprofile                            → the <ExistingProfileDialog> (treated as create-intent)
//   - partialaddress                             → a MultiuserActivation with an incomplete address, so
//                                                  the profile form renders with the address prefilled
//   - fallbackerror                              → the general <ActivationErrorFallback>
// Terminal invitation-code reasons (render the friendly <ActivationInvitationError> reason page,
// matching the pre-auth landing's `acceptinvitationdebug` values):
//   - expired / invalid / userslimitreached / inactive → the matching reason page
export type DebugErrorPage = 'googlerequired' | 'aadrequired' | 'msarequired' | 'facebookrequired' | 'aadonly' | 'denied' | 'alreadyhasaccess' | 'alternatemerge' | 'alternateblocked' | 'existingprofile' | 'partialaddress' | 'fallbackerror' | 'expired' | 'invalid' | 'userslimitreached' | 'inactive';

const ACCEPT_INVITATION_DEBUG_PARAM = 'acceptinvitationdebug';

export const getDebugErrorPage = (search = window.location.search): DebugErrorPage | null => {
  const value = new URLSearchParams(search).get(ACCEPT_INVITATION_DEBUG_PARAM)?.toLowerCase();
  switch (value) {
    case 'googlerequired':
    case 'aadrequired':
    case 'msarequired':
    case 'facebookrequired':
    case 'aadonly':
    case 'denied':
    case 'alreadyhasaccess':
    case 'alternatemerge':
    case 'alternateblocked':
    case 'existingprofile':
    case 'partialaddress':
    case 'fallbackerror':
    case 'expired':
    case 'invalid':
    case 'userslimitreached':
    case 'inactive':
      return value;
    default:
      return null;
  }
};

// Path of the legacy server-rendered accept-invitation controller. Hitting it unauthenticated
// (after sign-out) bounces to the pre-auth landing page, applying the server's intent/view carries.
const ACTIVATE_MSA_USER_PATH = '/ActivateMsaUser';
// The logout endpoint (serverContext.Urls.SignOutUrl) binds its return URL from the `RURL` query
// param (AdsAppUI LogoutController → MvcConstants.QueryReturnUrl = "RURL"): after sign-out it
// redirects there. MVC binds it case-insensitively.
const RETURN_URL_PARAM = 'RURL';

// Builds an `/ActivateMsaUser?Code=…` URL (plus any extra params, e.g. `intent=create`) to use as
// the post-sign-out return target.
const buildActivateMsaUserReturnUrl = (code: string, extraParams: Record<string, string> = {}): string => {
  const params = new URLSearchParams();
  if (code) {
    params.set('Code', code);
  }
  Object.entries(extraParams).forEach(([key, value]) => params.set(key, value));
  const query = params.toString();
  return query ? `${ACTIVATE_MSA_USER_PATH}?${query}` : ACTIVATE_MSA_USER_PATH;
};

// Takes the canonical sign-out URL and sets its `RURL` return param to `returnUrl`, preserving the
// rest of the (possibly relative) sign-out URL. Any pre-existing rurl/RURL is removed first so the
// server doesn't bind a stale one. Returns '' when no sign-out URL is configured.
const buildSignOutUrlWithReturn = (signOutUrl: string | undefined, returnUrl: string, win: Window): string => {
  if (!signOutUrl) {
    return '';
  }
  const isAbsolute = /^https?:\/\//i.test(signOutUrl);
  const base = win?.location?.origin || 'http://localhost';
  const url = new URL(signOutUrl, base);
  Array.from(url.searchParams.keys())
    .filter(key => key.toLowerCase() === 'rurl')
    .forEach(key => url.searchParams.delete(key));
  url.searchParams.set(RETURN_URL_PARAM, returnUrl);
  return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
};

// Signs the user out via the canonical logout endpoint (`serverContext.Urls.SignOutUrl`, which
// honors the `RURL` return param) and returns to `/ActivateMsaUser?Code=…` with the given params.
// On that unauthenticated return hop the server decides where to go from the params alone — no
// dedicated server URLs are needed (AdsAppUI PR 6836740 pattern): we tell the server what we want
// via params like `intent=create` (→ landing "Create an account" view) or `idP=AAD` (→ straight to
// AAD sign-in). With no extra params it lands on the default invitation choice view.
// Returns '' when no sign-out URL is configured (caller then no-ops).
const buildSignOutToActivateMsaUser = (code: string, extraParams: Record<string, string>, win: Window): string => {
  const serverUrls = (win as any)?.serverContext?.Urls || {};
  return buildSignOutUrlWithReturn(serverUrls.SignOutUrl, buildActivateMsaUserReturnUrl(code, extraParams), win);
};

// The accept-invitation client-side-flow flight alias. This code only runs on the NEW post-auth
// client page, so we carry `aicsf=1` on every /ActivateMsaUser target we build: it forces
// ActivateMsaUserController to route the user back to the new post-auth page instead of
// re-evaluating the (ramping) pilot on the unauthenticated hop — which could otherwise bounce them
// onto the legacy flow. The server already honors this explicit value (no server change needed).
const CLIENT_FLOW_ALIAS = 'aicsf';

// "Use a different email" (existing-profile dialog) — sign out and land back on the pre-auth
// "Create an account" view. Signs out via the canonical `SignOutUrl`, returning to
// `/ActivateMsaUser?Code=…&intent=create`; on that unauthenticated return hop the controller appends
// `view=create` (server change S-2), so the user lands directly on the landing "Create an account"
// view. This is the round-trip AdsAppUI PR 6836740 expects the client to build — the server
// deliberately did NOT add a dedicated `acceptInvitationSwitchEmail` URL. Returns '' when no sign-out
// URL is configured (caller then no-ops).
export const buildSwitchEmailUrl = (code: string, win: Window = window): string => (
  buildSignOutToActivateMsaUser(code, { intent: 'create', [CLIENT_FLOW_ALIAS]: '1' }, win)
);

// Builds the ABSOLUTE post-login return target — `<origin>/ActivateMsaUser?Code=…&aicsf=1` — that the
// federated-login endpoint redirects to after the user authenticates with the chosen IdP. It is
// absolute (not relative) so the login server's 302 lands on the correct CCUI host, and carries
// `aicsf=1` so the user returns to the NEW post-auth page. No `idP` is needed here: once re-authed as
// the right provider, ActivationOptions no longer reports ActivateWithAlternate.
const buildFederatedReturnUrl = (code: string, win: Window): string => {
  const relative = buildActivateMsaUserReturnUrl(code, { [CLIENT_FLOW_ALIAS]: '1' });
  const origin = win?.location?.origin || '';
  return origin ? `${origin}${relative}` : relative;
};

// Routes an ALREADY-authenticated user straight to a specific IdP to RE-authenticate (e.g. they
// signed in with MSA but the invitation needs Google). Uses `serverContext.Urls.FederatedLogin` — the
// same per-IdP federated-login endpoint the pre-auth landing page uses — stamped with `idP=<provider>`
// and `RURL=<absolute /ActivateMsaUser?Code=…&aicsf=1>`. Unlike `SignOutUrl` (which only signs out and
// lands on the generic login chooser), FederatedLogin sends the browser directly to the requested IdP.
//
// FALLBACK: until the server exposes `FederatedLogin` on the post-auth page's serverContext (see the
// design doc §5b.4 S-3), we degrade to the previous sign-out round-trip (`SignOutUrl` + `RURL=
// /ActivateMsaUser?idP=…`). Returns '' only when neither URL is configured (caller then no-ops).
type FederatedSignInOptions = {
  // Clear the CURRENT identity before re-authenticating. Required for the Microsoft providers:
  // going straight to FederatedLogin leaves ccmsa/ccaad/ccgoogle set, so the IdP silently
  // re-authenticates the SAME account and the user lands back on the same error page.
  signOutFirst?: boolean;
  // The account we are asking the user to switch to, passed to the IdP as a sign-in hint so the
  // page opens pre-filled instead of on a bare account picker (the legacy flow does the same via
  // Urls.FederatedLogin's `uname`). Omitted entirely when unknown.
  loginHint?: string | null;
};

// Login-hint query param. `LoginController.Federated` binds it to the IdP sign-in hint; on the
// sign-out round-trip it rides the RURL and `ActivateMsaUserController.Index` forwards it to the
// auth challenge. Servers predating that binding ignore the unknown param, so emitting it is inert.
const LOGIN_HINT_PARAM = 'uname';

// `LoginController.Federated` matches `case var _ when IsFirstPartyUser(username)` BEFORE it switches
// on idP, so a hint ending in @microsoft.com routes the user to AAD no matter which provider was
// requested. Suppress the hint rather than the provider, so a first-party address registered against
// Google/Facebook still reaches the right sign-in page.
const isFirstPartyHint = (loginHint?: string | null): boolean => /@microsoft\.com$/i.test(loginHint || '');

const buildFederatedSignInUrl = (
  code: string,
  idP: string,
  win: Window,
  { signOutFirst, loginHint }: FederatedSignInOptions = {}
): string => {
  const hintParam = loginHint ? { [LOGIN_HINT_PARAM]: loginHint } : {};

  // The sign-out round-trip is the only way to clear the current identity, and LogoutController
  // honors a direct `RURL` ONLY when its path is exactly /ActivateMsaUser
  // (IsSafeActivateMsaUserReturnUrl) -- a FederatedLogin RURL would be dropped on the generic login
  // landing page instead. So route through /ActivateMsaUser?idP=… and let the server challenge.
  if (signOutFirst) {
    return buildSignOutToActivateMsaUser(code, { idP, ...hintParam, [CLIENT_FLOW_ALIAS]: '1' }, win);
  }

  const federatedHint = isFirstPartyHint(loginHint) ? undefined : loginHint;
  const serverUrls = (win as any)?.serverContext?.Urls || {};
  const federatedLogin = serverUrls.FederatedLogin as string | undefined;
  if (federatedLogin) {
    try {
      const url = new URL(federatedLogin, win?.location?.origin || 'http://localhost');
      // Remove any pre-existing idP/RURL/uname variants case-insensitively first — the server binds
      // these params case-insensitively, so a configured URL carrying `idp`/`rurl`/`uname` would
      // otherwise keep the stale value and end up with a duplicate parameter.
      Array.from(url.searchParams.keys())
        .filter((key) => {
          const lower = key.toLowerCase();
          return lower === 'idp' || lower === 'rurl' || lower === LOGIN_HINT_PARAM;
        })
        .forEach(key => url.searchParams.delete(key));
      url.searchParams.set('idP', idP);
      if (federatedHint) {
        url.searchParams.set(LOGIN_HINT_PARAM, federatedHint);
      }
      url.searchParams.set(RETURN_URL_PARAM, buildFederatedReturnUrl(code, win));
      return url.toString();
    } catch {
      // Malformed FederatedLogin — fall through to the sign-out fallback below.
    }
  }
  return buildSignOutToActivateMsaUser(code, { idP, ...hintParam, [CLIENT_FLOW_ALIAS]: '1' }, win);
};

// "Continue with Microsoft" on the "Sign in with Microsoft work or school account" error page —
// signs the CURRENT identity out, then returns to /ActivateMsaUser?idP=AAD so the server issues the
// AAD challenge. `loginHint` pre-fills the AAD sign-in page with the matched account.
// Returns '' when no sign-out URL is configured.
export const buildAadSignInUrl = (code: string, win: Window = window, loginHint?: string | null): string => (
  buildFederatedSignInUrl(code, 'AAD', win, { signOutFirst: true, loginHint })
);

// "Continue with Microsoft" on the "Sign in with Microsoft personal account" error page — the mirror
// of buildAadSignInUrl: re-authenticate DIRECTLY with MSA (personal account) via the federated-login
// endpoint. Returns '' when neither a federated-login nor a sign-out URL is configured.
export const buildMsaSignInUrl = (code: string, win: Window = window, loginHint?: string | null): string => (
  buildFederatedSignInUrl(code, 'MSA', win, { signOutFirst: true, loginHint })
);

// "Sign in with Google" on the "Sign in with your Google account" error page — re-authenticate
// DIRECTLY with Google via the federated-login endpoint. Returns '' when neither a federated-login nor
// a sign-out URL is configured.
// Google keeps the direct federated hop: it re-prompts on its own (14% repeat-visit rate vs 70-86%
// for the Microsoft providers), so a sign-out would only add a redirect to a working path.
export const buildGoogleSignInUrl = (code: string, win: Window = window, loginHint?: string | null): string => (
  buildFederatedSignInUrl(code, 'Google', win, { loginHint })
);

// "Sign in with Facebook" on the "Sign in with your Facebook account" error page — re-authenticate
// DIRECTLY with Facebook via the federated-login endpoint. Returns '' when neither a federated-login
// nor a sign-out URL is configured.
export const buildFacebookSignInUrl = (code: string, win: Window = window, loginHint?: string | null): string => (
  buildFederatedSignInUrl(code, 'Facebook', win, { loginHint })
);

// Re-authentication target for a 401 `ReauthRequired` (see accept-invitation-page). The server
// supplies its own challenge endpoint, which 302s to the IdP and — because the browser still holds a
// live IdP session — straight back, repopulating the server-side token cache without prompting. Only
// SAME-ORIGIN targets are honored so a tampered/replayed response cannot turn this into an open
// redirect; the server does the onward hop to the IdP itself. Returns '' when the URL is absent,
// malformed, or off-origin, and the caller falls back to the sign-out round-trip.
export const buildReauthUrl = (authUrl?: string | null, win: Window = window): string => {
  if (!authUrl) {
    return '';
  }

  try {
    const { origin } = win.location;
    const url = new URL(authUrl, origin);
    return url.origin === origin ? url.toString() : '';
  } catch {
    return '';
  }
};

// Default "back to invitation" logout, used by the work-account error page's "Go back" action and
// the general error-page fallback — sign out and return to the pre-auth invitation landing page.
//
// We stamp `aicsf=1` on the return target (`/ActivateMsaUser?Code=…&aicsf=1`) so the server keeps the
// user on the NEW client-side flow after sign-out. This matters because "Go back" is reached from a
// post-auth error page, and the round-trip goes:
//   post-auth error page → sign out → return UNAUTHENTICATED to /ActivateMsaUser?Code=…&aicsf=1.
// Without the alias, an unauthenticated hit re-evaluates the (ramping) pilot and, for an
// expired/invalid code, ActivateMsaUserController's server-side validate throws and surfaces the
// LEGACY "invitation expired" page (bug 10930871). With `aicsf=1`, the server can hand the request
// straight to the pre-auth client landing page (idp-selection-page), which re-validates client-side
// and renders the new pre-auth experience (choice view, or the new expired/invalid error page).
//
// NOTE: this alias by itself is NOT sufficient today — the current server only honors it for
// AUTHENTICATED requests (AdsAppUI PR 6899549). The matching server change is documented in
// REDIRECT-SCENARIOS.md (§ "Go back", server change S-GB): route the UNAUTHENTICATED `aicsf=1` hit to
// the pre-auth landing page BEFORE server-side validate. Returns '' when no sign-out URL is
// configured (caller then no-ops).
export const buildBackToInvitationUrl = (code: string, win: Window = window): string => (
  buildSignOutToActivateMsaUser(code, { [CLIENT_FLOW_ALIAS]: '1' }, win)
);

// Logout page for a 401 from a session-bound API (ActivationOptions / Activate — the session is
// invalid/expired). Navigates straight to the canonical sign-out endpoint so the user is signed out
// and can start over. Returns '' when no sign-out URL is configured (caller then no-ops).
export const buildLogoutUrl = (win: Window = window): string => {
  const serverUrls = (win as any)?.serverContext?.Urls || {};
  return (serverUrls.SignOutUrl || '') as string;
};
